import type { SupabaseClient } from "@supabase/supabase-js";

import {
  fetchGuardSiteGeofenceStatus,
  indexGeofenceStatusByAssignment,
  type GuardSiteGeofenceStatus,
} from "./geofence-status";
import { gpsAccuracyBandThresholds, type GpsAccuracyBandThresholds } from "./gps-accuracy-bands";
import { liveCheckinMaxAgeHours, type LiveCheckinMaxAgeHours } from "./live-checkin-max-age";
import { replayRetentionDays, type ReplayRetentionDays } from "./replay-retention";

// Duty tracking read models (PRD-001J J3). Two read paths, both shipped by the J1 foundation:
//   1. public.get_account_duty_tracking_policy(active_account_id) — security-invoker RPC that
//      returns the active account duty policy, or safe disabled defaults. Account-admin read row.
//   2. public.duty_location_pings — append-only safety samples. RLS scopes reads to the caller's
//      own rows OR account members holding schedule.manage. We never expose user_id here; rows
//      join to the on-shift roster by route_assignment_id, which is already redacted upstream.
// All helpers fail safe (throw a console-safe message; callers render an error state). Pure
// mappers + staleness math sit underneath so the surfaces stay on a unit-tested core.

export interface DutyTrackingPolicy {
  enabled: boolean;
  requireClockIn: boolean;
  pingIntervalSeconds: number;
  locationScope: string;
  consentKey: string;
  geofenceAlertsEnabled: boolean;
  geofenceSupervisorPushEnabled: boolean;
  geofenceMinAccuracyMeters: number;
  geofenceExitBufferMeters: number;
  geofenceExitConfirmPings: number;
  geofenceEnterConfirmPings: number;
  geofenceCooldownSeconds: number;
  /**
   * Unanswered Duty Call offer hold. A sibling field on the same policy row, deliberately
   * independent of `enabled`: turning duty-location pings off must not turn offer expiry off.
   */
  unansweredOfferHoldEnabled: boolean;
  unansweredOfferHoldSeconds: number;
  /** Account calendar depth for Command Replay. Independent of whether new pings are enabled. */
  replayRetentionDays: ReplayRetentionDays;
  /** Command Live Recent check-ins paint window in hours. Independent of duty ping enablement. */
  liveCheckinMaxAgeHours: LiveCheckinMaxAgeHours;
  /**
   * GPS accuracy band ceilings in metres shared by Timeclock labels and Command Live pin identity.
   * Exposed even while duty tracking is disabled (placement-threshold precedent), because the
   * report keeps grading punches from the same active row either way.
   */
  gpsAccuracyHighMaxMeters: number;
  gpsAccuracyMediumMaxMeters: number;
}

/** Mirrors the RPC's disabled-default payload (no active policy row → tracking is off). */
export const DISABLED_DUTY_POLICY: DutyTrackingPolicy = {
  enabled: false,
  requireClockIn: true,
  pingIntervalSeconds: 300,
  locationScope: "foreground_scan_only",
  consentKey: "field_duty",
  geofenceAlertsEnabled: false,
  geofenceSupervisorPushEnabled: false,
  geofenceMinAccuracyMeters: 50,
  geofenceExitBufferMeters: 15,
  geofenceExitConfirmPings: 2,
  geofenceEnterConfirmPings: 1,
  geofenceCooldownSeconds: 300,
  // The hosted RPC falls back to enabled/120s when no policy row exists, but this constant is the
  // shape used when the payload itself could not be read. Claiming a hold is running when we could
  // not read one would put a countdown on the Calls tile for an account that has expiry off, so
  // the unreadable case says "off" and the tile simply shows no hold line.
  unansweredOfferHoldEnabled: false,
  unansweredOfferHoldSeconds: 120,
  replayRetentionDays: 30,
  liveCheckinMaxAgeHours: 24,
  gpsAccuracyHighMaxMeters: 25,
  gpsAccuracyMediumMaxMeters: 100,
};

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Finite number or numeric string. `duty_location_pings.gps_accuracy_meters` is numeric(9,2),
 * and PostgREST serializes numerics as strings, so the plain reader above would drop every
 * accuracy on the floor and make every officer read "unavailable".
 */
function asNumeric(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Both ceilings or neither: a pair that is unreadable or inverted falls back to 25 / 100 as a
 * pair, exactly as `app_private.account_gps_accuracy_bands` does for a malformed row.
 */
function dutyPolicyGpsAccuracyBandFields(
  highMaxMeters: number | null,
  mediumMaxMeters: number | null,
): Pick<DutyTrackingPolicy, "gpsAccuracyHighMaxMeters" | "gpsAccuracyMediumMaxMeters"> {
  const thresholds = gpsAccuracyBandThresholds(highMaxMeters, mediumMaxMeters);
  return {
    gpsAccuracyHighMaxMeters: thresholds.highMaxMeters,
    gpsAccuracyMediumMaxMeters: thresholds.mediumMaxMeters,
  };
}

/** The thresholds the presentation layer reads, straight from a resolved policy. */
export function dutyPolicyGpsAccuracyThresholds(
  policy: Pick<DutyTrackingPolicy, "gpsAccuracyHighMaxMeters" | "gpsAccuracyMediumMaxMeters">,
): GpsAccuracyBandThresholds {
  return gpsAccuracyBandThresholds(policy.gpsAccuracyHighMaxMeters, policy.gpsAccuracyMediumMaxMeters);
}

/** Map the jsonb policy payload, falling back to safe disabled defaults field-by-field. */
export function mapDutyTrackingPolicy(raw: unknown): DutyTrackingPolicy {
  if (raw === null || typeof raw !== "object") {
    return { ...DISABLED_DUTY_POLICY };
  }
  const record = raw as Record<string, unknown>;
  return {
    enabled: record.enabled === true,
    requireClockIn: record.requireClockIn !== false,
    pingIntervalSeconds: asNumber(record.pingIntervalSeconds) ?? DISABLED_DUTY_POLICY.pingIntervalSeconds,
    locationScope: asString(record.locationScope) ?? DISABLED_DUTY_POLICY.locationScope,
    consentKey: asString(record.consentKey) ?? DISABLED_DUTY_POLICY.consentKey,
    geofenceAlertsEnabled: record.geofenceAlertsEnabled === true,
    geofenceSupervisorPushEnabled: record.geofenceSupervisorPushEnabled === true,
    geofenceMinAccuracyMeters:
      asNumber(record.geofenceMinAccuracyMeters) ?? DISABLED_DUTY_POLICY.geofenceMinAccuracyMeters,
    geofenceExitBufferMeters:
      asNumber(record.geofenceExitBufferMeters) ?? DISABLED_DUTY_POLICY.geofenceExitBufferMeters,
    geofenceExitConfirmPings:
      asNumber(record.geofenceExitConfirmPings) ?? DISABLED_DUTY_POLICY.geofenceExitConfirmPings,
    geofenceEnterConfirmPings:
      asNumber(record.geofenceEnterConfirmPings) ?? DISABLED_DUTY_POLICY.geofenceEnterConfirmPings,
    geofenceCooldownSeconds:
      asNumber(record.geofenceCooldownSeconds) ?? DISABLED_DUTY_POLICY.geofenceCooldownSeconds,
    unansweredOfferHoldEnabled: record.unansweredOfferHoldEnabled === true,
    unansweredOfferHoldSeconds:
      asNumber(record.unansweredOfferHoldSeconds)
      ?? DISABLED_DUTY_POLICY.unansweredOfferHoldSeconds,
    replayRetentionDays: replayRetentionDays(asNumber(record.replayRetentionDays)),
    liveCheckinMaxAgeHours: liveCheckinMaxAgeHours(asNumber(record.liveCheckinMaxAgeHours)),
    ...dutyPolicyGpsAccuracyBandFields(
      asNumber(record.gpsAccuracyHighMaxMeters),
      asNumber(record.gpsAccuracyMediumMaxMeters),
    ),
  };
}

/** Resolve the account's active duty-tracking policy (or disabled defaults). Throws safe. */
export async function fetchDutyTrackingPolicy(
  client: SupabaseClient,
  accountId: string,
): Promise<DutyTrackingPolicy> {
  const { data, error } = await client.rpc("get_account_duty_tracking_policy", {
    active_account_id: accountId,
  });
  if (error !== null) {
    throw new Error("Could not load the duty tracking policy.");
  }
  return mapDutyTrackingPolicy(data);
}

export interface DutyPing {
  routeAssignmentId: string | null;
  dutyCallId: string | null;
  serverReceivedAt: string | null;
  deviceTime: string | null;
  lat: number | null;
  lng: number | null;
  /**
   * Reported GPS accuracy radius in metres, or null when the device did not report one. Present
   * on the table since the duty-tracking foundation; Console simply never selected it, so Live
   * had no honest way to say how good a fix was (lock Q11 A allows accuracy, never coordinates).
   */
  gpsAccuracyMeters: number | null;
  pingSource: string | null;
}

export interface DutySessionPunch {
  routeAssignmentId: string | null;
  punchType: string | null;
  serverReceivedAt: string | null;
  deviceTime: string | null;
}

export type DutySessionState = "clocked_in" | "clocked_out" | "on_break" | "unknown";

export interface DutySession {
  state: DutySessionState;
  startedAt: string | null;
  lastPunchAt: string | null;
  lastPunchType: string | null;
}

export function mapDutyPing(raw: Record<string, unknown>): DutyPing {
  return {
    routeAssignmentId: asString(raw.route_assignment_id),
    dutyCallId: asString(raw.duty_call_id),
    serverReceivedAt: asString(raw.server_received_at),
    deviceTime: asString(raw.device_time),
    lat: asNumber(raw.lat),
    lng: asNumber(raw.lng),
    gpsAccuracyMeters: asNumeric(raw.gps_accuracy_meters),
    pingSource: asString(raw.ping_source),
  };
}

export function mapDutySessionPunch(raw: Record<string, unknown>): DutySessionPunch {
  return {
    routeAssignmentId: asString(raw.route_assignment_id),
    punchType: asString(raw.punch_type),
    serverReceivedAt: asString(raw.server_received_at),
    deviceTime: asString(raw.device_time),
  };
}

const DUTY_PING_LIMIT = 200;
const DUTY_TIMECLOCK_LIMIT = 500;
export const DUTY_TRAIL_LIMIT = 500;
export const DUTY_TRAIL_WINDOW_HOURS = 24;
export const DUTY_PING_COLUMNS =
  "route_assignment_id, duty_call_id, server_received_at, device_time, lat, lng, gps_accuracy_meters, ping_source";
// Existing management projection only. No guard ref, display name, device id, notes, or GPS
// columns are requested for the duty-session gate.
export const DUTY_TIMECLOCK_COLUMNS =
  "route_assignment_id, punch_type, server_received_at, device_time";

/** Oldest server_received_at included in a duty breadcrumb trail (policy window). */
export function dutyTrailSinceIso(nowMs: number, windowHours: number = DUTY_TRAIL_WINDOW_HOURS): string {
  const safeHours = windowHours > 0 ? windowHours : DUTY_TRAIL_WINDOW_HOURS;
  return new Date(nowMs - safeHours * 3_600_000).toISOString();
}

/** Recent duty pings for the account, newest first. RLS enforces management scope. Throws safe. */
export async function fetchRecentDutyPings(
  client: SupabaseClient,
  accountId: string,
  limit: number = DUTY_PING_LIMIT,
): Promise<DutyPing[]> {
  const { data, error } = await client
    .from("duty_location_pings")
    .select(DUTY_PING_COLUMNS)
    .eq("account_id", accountId)
    .order("server_received_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load duty location pings.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapDutyPing(row as Record<string, unknown>));
}

/**
 * Latest host-authorized positions for active assigned duty on Command Live.
 *
 * The RPC applies the account policy's 4x interval window and active-assignment gate before
 * coordinates leave Postgres. An explicit, roster-bounded assignment list supports the existing
 * filtered keep-visible exception. This remains separate from `fetchRecentDutyPings`: the roster
 * and historical desks still need retained last-seen rows after a Live dot expires.
 */
export async function fetchAssignedDutyLivePings(
  client: SupabaseClient,
  accountId: string,
  keepVisibleAssignmentIds: readonly string[] = [],
): Promise<DutyPing[]> {
  const { data, error } = await client.rpc("list_assigned_duty_live_presence", {
    active_account_id: accountId,
    p_keep_visible_assignment_ids: [...keepVisibleAssignmentIds],
  });
  if (error !== null) {
    throw new Error("Could not load current assigned duty locations.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapDutyPing(row as Record<string, unknown>));
}

/** Recent safe punch state for assignment-level duty gating. Throws a console-safe message. */
export async function fetchRecentDutySessionPunches(
  client: SupabaseClient,
  accountId: string,
  limit: number = DUTY_TIMECLOCK_LIMIT,
): Promise<DutySessionPunch[]> {
  const { data, error } = await client
    .from("management_timeclock_log")
    .select(DUTY_TIMECLOCK_COLUMNS)
    .eq("account_id", accountId)
    .order("device_time", { ascending: false })
    .order("server_received_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load duty clock-in state.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((row) => mapDutySessionPunch(row as Record<string, unknown>));
}

/** Chronological duty pings for one assignment (breadcrumb trail). RLS enforces management scope. */
export async function fetchDutyPingTrail(
  client: SupabaseClient,
  accountId: string,
  routeAssignmentId: string,
  options: {
    limit?: number;
    sinceIso?: string;
  } = {},
): Promise<DutyPing[]> {
  if (routeAssignmentId.length === 0) {
    return [];
  }
  const limit = options.limit ?? DUTY_TRAIL_LIMIT;
  const sinceIso = options.sinceIso ?? dutyTrailSinceIso(Date.now());
  const { data, error } = await client
    .from("duty_location_pings")
    .select(DUTY_PING_COLUMNS)
    .eq("account_id", accountId)
    .eq("route_assignment_id", routeAssignmentId)
    .gte("server_received_at", sinceIso)
    .order("server_received_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load the guard duty trail.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapDutyPing(row as Record<string, unknown>))
    .sort((left, right) => pingMillis(left) - pingMillis(right));
}

/** Chronological duty pings stamped to one Call inside the same 24 hour Focus budget. */
export async function fetchDutyCallPingTrail(
  client: SupabaseClient,
  accountId: string,
  dutyCallId: string,
  options: {
    limit?: number;
    sinceIso?: string;
  } = {},
): Promise<DutyPing[]> {
  if (dutyCallId.length === 0) return [];
  const limit = options.limit ?? DUTY_TRAIL_LIMIT;
  const sinceIso = options.sinceIso ?? dutyTrailSinceIso(Date.now());
  const { data, error } = await client
    .from("duty_location_pings")
    .select(DUTY_PING_COLUMNS)
    .eq("account_id", accountId)
    .eq("duty_call_id", dutyCallId)
    .gte("server_received_at", sinceIso)
    .order("server_received_at", { ascending: false })
    .limit(limit);
  if (error !== null) {
    throw new Error("Could not load the Call location story.");
  }
  if (!Array.isArray(data)) return [];
  return data
    .map((row) => mapDutyPing(row as Record<string, unknown>))
    .sort((left, right) => pingMillis(left) - pingMillis(right));
}

function pingMillis(ping: DutyPing): number {
  const iso = ping.serverReceivedAt ?? ping.deviceTime;
  if (typeof iso !== "string" || iso.length === 0) {
    return Number.NEGATIVE_INFINITY;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

function timestampMillis(iso: string | null): number {
  if (iso === null) {
    return Number.NEGATIVE_INFINITY;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

function comparePunchesNewestFirst(left: DutySessionPunch, right: DutySessionPunch): number {
  // Match the existing duty clock RPC exactly: device action time first, receipt time as the
  // deterministic tie-break. A later off-duty punch must win an equal device timestamp.
  const deviceDelta =
    timestampMillis(right.deviceTime ?? right.serverReceivedAt) -
    timestampMillis(left.deviceTime ?? left.serverReceivedAt);
  if (deviceDelta !== 0) {
    return deviceDelta;
  }
  return timestampMillis(right.serverReceivedAt) - timestampMillis(left.serverReceivedAt);
}

function punchIso(punch: DutySessionPunch): string | null {
  return punch.deviceTime ?? punch.serverReceivedAt;
}

const CLOCKED_IN_PUNCH_TYPES = new Set(["clock_in", "break_end"]);

/** Resolve the current clock-in session for each assignment without joining on a raw user id. */
export function indexDutySessionsByRouteAssignment(
  punches: readonly DutySessionPunch[],
): Record<string, DutySession> {
  const grouped: Record<string, DutySessionPunch[]> = {};
  for (const punch of punches) {
    const key = punch.routeAssignmentId;
    if (key === null) {
      continue;
    }
    (grouped[key] ??= []).push(punch);
  }

  const sessions: Record<string, DutySession> = {};
  for (const [assignmentId, rows] of Object.entries(grouped)) {
    rows.sort(comparePunchesNewestFirst);
    const latest = rows[0];
    if (latest === undefined) {
      continue;
    }
    const state: DutySessionState =
      latest.punchType === "clock_out"
        ? "clocked_out"
        : latest.punchType === "break_start"
          ? "on_break"
          : latest.punchType !== null && CLOCKED_IN_PUNCH_TYPES.has(latest.punchType)
            ? "clocked_in"
            : "unknown";
    let startedAt: string | null = null;
    if (state === "clocked_in") {
      for (const row of rows) {
        if (row !== latest && row.punchType === "clock_out") {
          break;
        }
        if (row.punchType === "clock_in") {
          startedAt = punchIso(row);
          break;
        }
      }
    }
    sessions[assignmentId] = {
      state,
      startedAt,
      lastPunchAt: punchIso(latest),
      lastPunchType: latest.punchType,
    };
  }
  return sessions;
}

/** Keep the latest ping per route_assignment_id (rows without one can't map to a roster row). */
export function indexLatestByRouteAssignment(pings: readonly DutyPing[]): Record<string, DutyPing> {
  const latest: Record<string, DutyPing> = {};
  for (const ping of pings) {
    const key = ping.routeAssignmentId;
    if (key === null) {
      continue;
    }
    const existing = latest[key];
    if (existing === undefined || pingMillis(ping) > pingMillis(existing)) {
      latest[key] = ping;
    }
  }
  return latest;
}

export interface DutyPresenceSnapshot {
  enabled: boolean;
  requireClockIn: boolean;
  intervalSeconds: number;
  pingsByRouteAssignment: Record<string, DutyPing>;
  sessionsByRouteAssignment: Record<string, DutySession>;
  geofenceAlertsEnabled: boolean;
  geofenceByRouteAssignment: Record<string, GuardSiteGeofenceStatus>;
}

export const DISABLED_DUTY_PRESENCE: DutyPresenceSnapshot = {
  enabled: false,
  requireClockIn: DISABLED_DUTY_POLICY.requireClockIn,
  intervalSeconds: DISABLED_DUTY_POLICY.pingIntervalSeconds,
  pingsByRouteAssignment: {},
  sessionsByRouteAssignment: {},
  geofenceAlertsEnabled: false,
  geofenceByRouteAssignment: {},
};

type DutyPingReader = (client: SupabaseClient, accountId: string) => Promise<DutyPing[]>;

async function fetchDutyPresenceWithPingReader(
  client: SupabaseClient,
  accountId: string,
  readPings: DutyPingReader,
): Promise<DutyPresenceSnapshot> {
  const policy = await fetchDutyTrackingPolicy(client, accountId);
  if (!policy.enabled) {
    return {
      enabled: false,
      requireClockIn: policy.requireClockIn,
      intervalSeconds: policy.pingIntervalSeconds,
      pingsByRouteAssignment: {},
      sessionsByRouteAssignment: {},
      geofenceAlertsEnabled: policy.geofenceAlertsEnabled,
      geofenceByRouteAssignment: {},
    };
  }
  const [pings, punches, geofenceRows] = await Promise.all([
    readPings(client, accountId),
    fetchRecentDutySessionPunches(client, accountId),
    policy.geofenceAlertsEnabled ? fetchGuardSiteGeofenceStatus(client, accountId) : Promise.resolve([]),
  ]);
  return {
    enabled: true,
    requireClockIn: policy.requireClockIn,
    intervalSeconds: policy.pingIntervalSeconds,
    pingsByRouteAssignment: indexLatestByRouteAssignment(pings),
    sessionsByRouteAssignment: indexDutySessionsByRouteAssignment(punches),
    geofenceAlertsEnabled: policy.geofenceAlertsEnabled,
    geofenceByRouteAssignment: indexGeofenceStatusByAssignment(geofenceRows),
  };
}

/**
 * Roster presence keeps retained last-seen pings so STALE / Last ping remains honest after the
 * map's 4x window closes. Historical rows remain subject to the existing table RLS.
 */
export async function fetchDutyPresence(
  client: SupabaseClient,
  accountId: string,
): Promise<DutyPresenceSnapshot> {
  return fetchDutyPresenceWithPingReader(client, accountId, fetchRecentDutyPings);
}

/**
 * Command Live presence uses the host-bounded, active-assignment projection. Focus, Call,
 * Replay, SOS, and the roster do not use this function and retain their existing windows.
 */
export async function fetchLiveDutyPresence(
  client: SupabaseClient,
  accountId: string,
): Promise<DutyPresenceSnapshot> {
  return fetchDutyPresenceWithPingReader(client, accountId, fetchAssignedDutyLivePings);
}

// A guard is "stale" once their last duty ping is older than 2× the configured ping interval —
// they should have reported at least once in that window. AC-001J.3.2.
export const STALE_INTERVAL_MULTIPLIER = 2;

export type DutyFreshnessState = "fresh" | "stale" | "missing";

export interface DutyFreshness {
  state: DutyFreshnessState;
  /** Whole seconds since the last ping, or null when none has arrived. */
  ageSeconds: number | null;
  /** Staleness boundary in seconds (2× interval). */
  thresholdSeconds: number;
}

export type DutyPresenceReason =
  | "live"
  | "stale"
  | "pings_dark"
  | "policy_off"
  | "no_clock_in"
  | "duty_off";

export interface DutyDisplayState extends DutyFreshness {
  trackingActive: boolean;
  reason: DutyPresenceReason;
}

/** Classify a guard's last-ping freshness against the policy interval. Pure; clock passed in. */
export function dutyFreshness(
  lastPingIso: string | null | undefined,
  nowMs: number,
  intervalSeconds: number,
): DutyFreshness {
  const safeInterval = intervalSeconds > 0 ? intervalSeconds : DISABLED_DUTY_POLICY.pingIntervalSeconds;
  const thresholdSeconds = safeInterval * STALE_INTERVAL_MULTIPLIER;
  if (typeof lastPingIso !== "string" || lastPingIso.length === 0) {
    return { state: "missing", ageSeconds: null, thresholdSeconds };
  }
  const ms = new Date(lastPingIso).getTime();
  if (Number.isNaN(ms)) {
    return { state: "missing", ageSeconds: null, thresholdSeconds };
  }
  const ageSeconds = Math.max(0, Math.round((nowMs - ms) / 1000));
  return {
    state: ageSeconds > thresholdSeconds ? "stale" : "fresh",
    ageSeconds,
    thresholdSeconds,
  };
}

/**
 * Resolve one roster row without reviving an old last-known location. Clock-in is mandatory when
 * policy says so. Otherwise an open punch or active assignment is the duty gate.
 */
export function dutyDisplayState({
  policyEnabled,
  requireClockIn,
  assignmentStatus,
  session,
  ping,
  nowMs,
  intervalSeconds,
}: {
  policyEnabled: boolean;
  requireClockIn: boolean;
  assignmentStatus: string;
  session: DutySession | undefined;
  ping: DutyPing | undefined;
  nowMs: number;
  intervalSeconds: number;
}): DutyDisplayState {
  const freshness = dutyFreshness(ping?.serverReceivedAt ?? ping?.deviceTime ?? null, nowMs, intervalSeconds);
  if (!policyEnabled) {
    return { ...freshness, state: "missing", ageSeconds: null, trackingActive: false, reason: "policy_off" };
  }

  const isClockedIn = session?.state === "clocked_in";
  const hasActiveAssignment = assignmentStatus === "active";
  const assignmentEnded = assignmentStatus !== "active" && assignmentStatus !== "scheduled";
  const explicitlyOffDuty = session?.state === "clocked_out" || session?.state === "on_break";
  const trackingActive = !explicitlyOffDuty && (isClockedIn || (!requireClockIn && hasActiveAssignment));
  if (!trackingActive) {
    const reason: DutyPresenceReason =
      explicitlyOffDuty || assignmentEnded
        ? "duty_off"
        : requireClockIn
          ? "no_clock_in"
          : "duty_off";
    return { ...freshness, state: "missing", ageSeconds: null, trackingActive: false, reason };
  }

  if (freshness.state === "fresh") {
    return { ...freshness, trackingActive: true, reason: "live" };
  }
  if (freshness.state === "stale") {
    return { ...freshness, trackingActive: true, reason: "stale" };
  }
  return { ...freshness, trackingActive: true, reason: "pings_dark" };
}
